<template>
  <div class="risk" data-testid="risk">
    <HeadingDisplay
      class="risk__heading"
      size="medium"
      title="Qual nível de risco você está disposto a correr?"
    />
    <LoaderIndicator v-if="isFetchingRisks" with-container />
    <div v-else-if="risks">
      <RiskSelectionSlider
        class="risk__risk-selection"
        :options="risks.length"
        :max-width="880"
        :recommended-risk="recommendedRisk"
        @change="handleRiskChange"
      />

      <section class="risk__summary">
        <Grid :columns="[1, 1]" gutter="xm">
          <ListDisplay
            v-for="(item, index) in riskDetails"
            :key="index"
            class="risk__summary-item"
            :title="item.label"
            value=""
            :has-divider="item.divider"
          >
            <template #value-slot>
              <div class="risk__summary-value">
                <span>{{ item.value }}</span>
                <ButtonIconContextual
                  size="small"
                  :label="item.label"
                  icon="EF0080"
                  @click="openDetailsModal(item.summary)"
                />
              </div> </template
          ></ListDisplay>
        </Grid>
      </section>
    </div>

    <DetailsModal
      :is-open="isDetailsModalOpen"
      v-bind="modalDetailsData"
      @close="() => (isDetailsModalOpen = false)"
    />

    <ButtonContainer class="risk__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          :loading="isLoading"
          :disabled="isFetchingRisks"
          size="large"
          @click="onClick"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, Ref, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  LoaderIndicator,
  ListDisplay,
  Grid,
  ButtonIconContextual,
  HeadingDisplay,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { DetailsModal } from '@/modules/catalog/views/FundsAcquisition/components/DetailsModal';
import { RiskSelectionSlider } from '@/modules/common/components/RiskSelectionSlider';

import {
  getRisks,
  IRisk,
  IRiskSummary
} from '@/modules/catalog/services/funds';
import { IRiskData } from './types';

const router = useRouter();

const isLoading = inject('isLoading') as Ref<boolean>;
const data = inject('data') as Ref<IRiskData>;

const updateData = inject('updateData', (key: string, risk: object) => ({
  key,
  risk
}));

const isFetchingRisks = ref(true);
const risks = ref<IRisk[] | null>(null);
const selectedRisk = ref(1);

const isDetailsModalOpen = ref(false);
const modalDetailsData = ref<IRiskSummary>({
  label: '',
  description: '',
  details: null
});

const risk = computed(() => {
  return risks.value![selectedRisk.value - 1];
});

const riskDetails = computed(() => {
  const riskSelected = risk.value;

  return [
    {
      label: 'Meta de retorno',
      value: riskSelected.expectedReturn.formatted,
      summary: riskSelected.expectedReturn.summary,
      divider: true
    },
    {
      label: 'Máximo drawdown',
      value: riskSelected.drawdown.formatted,
      summary: riskSelected.drawdown.summary,
      divider: true
    },
    {
      label: 'Meta de volatilidade',
      value: riskSelected.volatility.formatted,
      summary: riskSelected.volatility.summary,
      divider: false
    },
    {
      label: 'Resgate em até',
      value: riskSelected.liquidity.formatted,
      summary: riskSelected.liquidity.summary,
      divider: false
    }
  ];
});

const investorType = computed(() => {
  return data.value.profile ? data.value.profile.value : 'Common';
});

const riskSelected = computed(() => {
  return {
    value: risk.value.risk.value,
    formatted: risk.value.risk.formatted,
    id: risk.value.apiId
  };
});

const recommendedRisk = computed(() => {
  return risks.value!.findIndex(risk => risk.recommended) + 1 || 1;
});

function handleRiskChange(index: number) {
  selectedRisk.value = index;
}

function onClick() {
  updateData('risk', riskSelected.value);
}

function openDetailsModal(summary: IRiskSummary) {
  modalDetailsData.value = summary;

  isDetailsModalOpen.value = true;
}

async function fetchRisks() {
  try {
    isFetchingRisks.value = true;

    const params = data.value.period
      ? {
          investorType: investorType.value,
          period: data.value.period.value
        }
      : {
          investorType: investorType.value,
          type: data.value.type
        };

    risks.value = await getRisks(params);

    selectedRisk.value = recommendedRisk.value;
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível carregar as informações. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });

    router.push({ name: 'catalog' });
  } finally {
    isFetchingRisks.value = false;
  }
}

onMounted(async () => {
  await fetchRisks();
});
</script>

<style lang="less" scoped>
.risk {
  max-width: 832px;
  margin: 0 auto;
  padding: 0 @size-spacing-x400 @size-spacing-x1800;

  &__heading {
    margin-top: @size-spacing-x1100;
  }

  &__risk-selection {
    margin-top: @size-spacing-x1200;
  }

  &__summary {
    margin-top: @size-spacing-x2000;
  }

  &__summary-item {
    margin-top: @size-spacing-x350;

    &:first-of-type,
    &:nth-of-type(2) {
      margin-top: 0;
    }
  }

  &__summary-value {
    display: flex;
    align-items: center;
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
